import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { useAggregate, useFind } from 'hooks';
import { Config } from 'configs';
import { getFullDateInCustomTimezone, utcDateToMillis, getDateInCustomTimezone, getFirstDayOfMonth, getLastDayOfMonth } from 'utils/dateManagement';
import { PageTitle } from 'components/layouts/pageTitle';
import { DataColumn, List, ListHeader } from 'components/lists';
import { extractRecordsFromFullArray, extractRecordsFromAggreagte } from 'utils/renderingHelpers';
import { Pagination } from 'components/lists/pagination';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Input } from 'components/formInputs/input';
import { PageContent } from 'components/layouts/pageContent';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { roundDecimal } from 'utils/roundingHelpers';
import { downloadCompanyInvoice } from 'api';

const invoicesPerPage = 6;

export const PaymentsBalance = () => {
  const { state: auth } = useContext(AuthContext);
  const { state: companyState, getCompanyRestaurants, shouldDisplayNoRestaurantBlock } = useContext(CompanyContext);
  const { alertController } = useContext(AlertContext);

  const [currentInvoicesPage, setCurrentInvoicesPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloadingInvoiceId, setIsDownloadingInvoiceId] = useState(null);

  const [filterFromDate, setFilterFromDate, filterFromDateRef] = useLocalStorage(`payments-filter-from-${auth.company?.company}`, getFirstDayOfMonth());
  const [filterToDate, setFilterToDate, filterToDateRef] = useLocalStorage(`payments-filter-to-${auth.company?.company}`, getLastDayOfMonth());
  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(`payments-filter-restaurant-${auth.company?.company}`, '');
  const [filterWaiter, setFilterWaiter, filterWaiterRef] = useLocalStorage(`payments-filter-waiter-${auth.company?.company}`, '');

  const currentFilteredData = useRef({ filtered: false, fromDate: '', toDate: '', restaurantName: '', employeeName: '', isEmployee: false }).current;

  const employeesRequestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.EMPLOYEES,
    // filter: `{ company: ObjectId('${auth.company?.company}') }`,
    cultureCode: Config.DEFAULTS.LANGUAGE,
    noFindOnLoad: true,
    companyIdForV2: auth.company?.company,
    resourceV2: 'AdminEmployee',
  };

  const [findEmployees, employees] = useFind(employeesRequestData);

  const invoicesRequestData = {
    collectionName: Config.DATA.COLLECTIONS.WEB_USERS,
    aggregateId: Config.DATA.AGGREGATES.GET_RESTAURANT_INVOICES,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { companyId: auth.company?.company };

      filter.restaurantId = filterRestaurantRef.value || '000000000000000000000000';
      setCurrentInvoicesPage(1);

      return filter;
    },
    pageSize: invoicesPerPage,
    pageNumber: currentInvoicesPage - 1,
    loadOnInit: false,
    dontRunOnInit: true,
    aggregateIdV2: 'AdminGetRestaurantInvoices',
    companyIdForV2: auth.company?.company,
  };

  const [fetchInvoices, invoicesResponse] = useAggregate(invoicesRequestData);

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_RESTAURANT_PAYMENTS,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { companyId: auth.company?.company };

      const fromDateMillis = utcDateToMillis(filterFromDateRef.value);
      const toDateMillis = utcDateToMillis(filterToDateRef.value ? `${filterToDateRef.value}T23:59:59Z` : null);

      filter.restaurantId = filterRestaurantRef.value || '000000000000000000000000';
      filter.fromMillis = fromDateMillis || 0;
      filter.toMillis = toDateMillis || 0;

      if (filterWaiterRef.value) {
        const employeeData = employees?.find((x) => x._id === filterWaiterRef.value);

        filter.employeeId = employeeData?._id || '000000000000000000000000';
        filter.waiterId = employeeData?.external_id || 'undefined';

        currentFilteredData.employeeName = `${employeeData?.first_name || ''} ${employeeData?.last_name || ''}`.trim();
        currentFilteredData.isEmployee = true;
      } else {
        currentFilteredData.isEmployee = false;
        currentFilteredData.employeeName = null;
      }

      currentFilteredData.filtered = true;

      currentFilteredData.fromDate = filterFromDateRef.value;
      currentFilteredData.toDate = filterToDateRef.value;

      const restaurantData = companyState.restaurants?.find((x) => x._id === filterRestaurantRef.value);

      currentFilteredData.restaurantName = restaurantData?.name;
      setCurrentPage(1);

      return filter;
    },
    loadOnInit: false,
    dontRunOnInit: true,
    aggregateIdV2: 'AdminGetRestaurantPayments',
    companyIdForV2: auth.company?.company,
  };

  const [fetchPayments, paymentsResponse, isLoading] = useAggregate(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  useEffect(() => {
    if (filterRestaurant) {
      findEmployees({ ...employeesRequestData, filter: `{ restaurant: ObjectId('${filterRestaurant}') }` }, true);
    }
  }, [filterRestaurant]);

  const { totalAmount, totalTips, totalPaid, totalCurrency } = useMemo(() => {
    const responseToUse = paymentsResponse;

    let totalAmountTemp = 0;
    let totalTipsTemp = 0;
    let tempCurrency = '';

    if (!responseToUse || responseToUse.length === 0) {
      return { totalAmount: 0, totalTips: 0 };
    }

    responseToUse.forEach((item) => {
      totalAmountTemp += ((item.amount || 0) - (item.discount || 0));
      totalTipsTemp += item.tips || 0;

      if (!tempCurrency) {
        tempCurrency = item.currency;
      }
    });

    return {
      totalAmount: roundDecimal(totalAmountTemp),
      totalTips: roundDecimal(totalTipsTemp),
      totalPaid: roundDecimal(totalAmountTemp) + roundDecimal(totalTipsTemp),
      totalCurrency: tempCurrency,
    };
  }, [paymentsResponse]);

  const filteredEmployees = useMemo(() => {
    if (!employees || employees.length === 0 || !filterRestaurant) {
      return [];
    }

    const filtered = employees.filter((x) => x.restaurant === filterRestaurant);

    return filtered || [];
  }, [filterRestaurant, employees]);

  const onFilterPayments = () => {
    fetchInvoices(true);

    fetchPayments(true, {
      ...requestData,
      aggregateId: filterWaiterRef.value
        ? Config.DATA.AGGREGATES.GET_RESTAURANT_EMPLOYEE_PAYMENTS
        : Config.DATA.AGGREGATES.GET_RESTAURANT_PAYMENTS,
      aggregateIdV2: filterWaiterRef.value
        ? 'AdminGetEmployeePayments'
        : 'AdminGetRestaurantPayments',
    });
  };

  const onClearFilters = () => {
    setFilterFromDate(getFirstDayOfMonth());
    setFilterToDate(getLastDayOfMonth());
    setFilterRestaurant('');
    onFilterPayments();
  };

  const onDownloadInvoice = async (item) => {
    const fileInfo = item && item.invoice && item.invoice.length > 0 ? item.invoice[0] : null;

    if (!fileInfo) {
      return;
    }

    setIsDownloadingInvoiceId(item._id);

    try {
      const urlResponse = await downloadCompanyInvoice({ token: auth.token, companyId: auth.company?.company, invoiceId: item._id, alertController });

      if (urlResponse && urlResponse.fileUrl) {
        const urlToFile = decodeURI(urlResponse.fileUrl);

        window.open(urlToFile, '_blank');
      }
    } catch (error) {
      console.error(error);
    }

    setIsDownloadingInvoiceId(null);
  };

  if (shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  const { paginatedResults: payments, totalCount } = extractRecordsFromFullArray(paymentsResponse, currentPage, Config.DEFAULTS.PAGE_SIZE);
  const { paginatedResults: invoices, totalCount: totalInvoicesCount } = extractRecordsFromAggreagte(invoicesResponse, currentInvoicesPage, invoicesPerPage);

  return (
    <ListLayout isLoading={isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle title="Payments" isTopLevel />

      <PageContent>
        <FilterBlock buttons={[
          {
            onPress: onClearFilters,
            text: 'Clear',
            class: 'bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
          },
          {
            onPress: onFilterPayments,
            text: 'Search',
            class: 'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
            disabled: !!isLoading,
            isLoading: !!isLoading,
          },
        ]}
        >
          <FilterField>
            <div>
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700">
                Date from
              </label>
              <div className="mt-1 relative text-sm text-gray-500">
                <Input
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  name="fromDate"
                  id="fromDate"
                  type="text"
                  value={filterFromDate}
                  onChange={(e) => {
                    setFilterFromDate(e.target.value);
                  }}
                  placeholder="YYYY-MM-DD"
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>
          </FilterField>
          <FilterField>
            <div>
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700">
                Date to
              </label>
              <div className="mt-1 relative text-sm text-gray-500">
                <Input
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  name="toDate"
                  id="toDate"
                  type="text"
                  value={filterToDate}
                  onChange={(e) => setFilterToDate(e.target.value)}
                  placeholder="YYYY-MM-DD"
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>
          </FilterField>
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              value={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
                setFilterWaiter('');
              }}
              options={[{ label: '-', value: '' }, ...(companyState.restaurants || []).map((restaurant) => ({
                label: restaurant.name,
                value: restaurant._id,
              }))]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
          <FilterField>
            <DropDownList
              showLabel
              id="employees"
              name="employees"
              label="Employee"
              value={filterWaiter}
              onChange={(e) => {
                setFilterWaiter(e.target.value);
              }}
              options={[{ label: '-', value: '' }, ...(filteredEmployees || []).map((employee) => ({
                label: `${employee.first_name || ''} ${employee.last_name || ''}`.trim(),
                value: employee._id,
              }))]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        {currentFilteredData.filtered && (
        <>
          <ListHeader
            title="Restaurant payouts"
            description="Received restaurant payouts"
          />

          <List source={invoices}>
            <DataColumn
              name="Period"
              dbColumnName="period"
              render={(x) => {
                return `${getDateInCustomTimezone(x.from, x.timezone)} - ${getDateInCustomTimezone(x.until, x.timezone)}`;
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            <DataColumn
              name="Payments for"
              dbColumnName="for_employees"
              render={(x) => {
                if (x.for_employees) {
                  return 'Tips';
                }

                return 'Orders';
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            <DataColumn
              name="Total"
              dbColumnName="total_order_sum"
              render={(x) => {
                if (!x.for_employees) {
                  return (x.total_order_sum || 0).toFixed(2);
                }

                if (x.employees && x.employees.length > 0) {
                  let total = 0;

                  x.employees.forEach((employee) => {
                    total += employee.total_tips;
                  });

                  return (roundDecimal(total)).toFixed(2);
                }

                return '';
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            <DataColumn
              name="Report"
              dbColumnName="invoice"
              render={(x) => {
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDownloadInvoice(x);
                    }}
                    className="font-medium text-indigo-600 hover:text-indigo-500"
                    disabled={!!isDownloadingInvoiceId}
                  >
                    Download
                  </button>
                );
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />

            <Pagination
              pageSize={invoicesPerPage}
              totalCount={totalInvoicesCount}
              currentPage={currentInvoicesPage}
              onPageChanged={async (pageNumber) => {
                const request = { ...invoicesRequestData, pageNumber: pageNumber - 1 };

                await fetchInvoices(false, request);
                setCurrentInvoicesPage(pageNumber);
              }}
            />
          </List>

          <div className="mb-12" />

          <ListHeader
            title="Payments"
            description="Payments in selected period"
          />

          <div className="my-10 max-w-8xl mx-auto">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 sm:px-6 py-5 grid grid-cols-3 gap-4">
                <div className="col-span-3">
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Restaurant: {currentFilteredData.restaurantName}</p>
                  {!!currentFilteredData.employeeName && <p className="mt-1 max-w-2xl text-sm text-gray-500">Employee: {currentFilteredData.employeeName}</p>}
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">from {currentFilteredData.fromDate} to {currentFilteredData.toDate}</p>
                </div>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  {!currentFilteredData.isEmployee && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Orders total</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{(totalAmount || 0).toFixed(2)} {totalCurrency}</dd>
                  </div>
                  )}
                  <div className={`${currentFilteredData.isEmployee ? 'bg-gray-50' : 'bg-white'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                    <dt className="text-sm font-medium text-gray-500">Tips total</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(totalTips || 0).toFixed(2)} {totalCurrency}
                    </dd>
                  </div>
                  {!currentFilteredData.isEmployee && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Total</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(totalPaid || 0).toFixed(2)} {totalCurrency}
                    </dd>
                  </div>
                  )}
                </dl>
              </div>
            </div>
          </div>

          <List source={payments}>
            <DataColumn
              name="Date"
              dbColumnName="paid_on"
              render={(x) => {
                return getFullDateInCustomTimezone(x.paid_on, x.timezone);
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            {!currentFilteredData.isEmployee && (
            <DataColumn
              name="Order Payment"
              dbColumnName="amount"
              render={(x) => {
                if (!x.amount || x.amount === 0) {
                  return '';
                }

                return `${((x.amount || 0) - (x.discount || 0)).toFixed(2)} ${x.currency}`;
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            )}
            <DataColumn
              name="Tips"
              dbColumnName="tips"
              render={(x) => {
                if (!x.tips || x.tips === 0) {
                  return '';
                }

                return `${(x.tips || 0).toFixed(2)} ${x.currency}`;
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            {!currentFilteredData.isEmployee && (
            <DataColumn
              name="Total"
              dbColumnName="total"
              render={(x) => {
                return `${(((x.amount || 0) - (x.discount || 0)) + (x.tips || 0)).toFixed(2)} ${x.currency}`;
              }}
              headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
            />
            )}

            <Pagination
              pageSize={Config.DEFAULTS.PAGE_SIZE}
              totalCount={totalCount}
              currentPage={currentPage}
              onPageChanged={(pageNumber) => {
                setCurrentPage(pageNumber);
              }}
            />
          </List>
        </>
        )}
      </PageContent>
    </ListLayout>
  );
};
